"use client";

import NextForm from "next/form";

import { Input, Text, View } from "@/components";

import type { FormProps } from "@/components/types";
import type { JSX } from "react";

/**
 * @name Form
 * @description A flexible and reusable form component built with Next.js specific form enhancement (`NextForm`).
 * It orchestrates the rendering of form fields and custom content.
 *
 * Key features:
 * - **Dynamic Field Rendering**: Iterates over a provided `fields` array to render labelled input groups using `View`, `Text`, and `Input` atoms.
 * - **Server Actions Support**: Designed to work seamlessly with Server Actions via the `action` prop.
 * - **Customizable Layout**: Supports custom styling via `className` and arbitrary children injection for complex layouts.
 * - **Validation Feedback**: Displays an error message if the form is initialized without fields or children.
 *
 * Use this component to build data entry forms that require standard inputs along with custom elements like textareas or checkboxes.
 *
 * @component
 * @example
 * ```tsx
 * // Example usage with server action and custom fields
 * <FormSimple
 *   action={handleLogin}
 *   fields={[
 *     { id: "email", label: "Email Address", name: "email", type: "email", placeholder: "user@example.com", required: true },
 *     { id: "password", label: "Password", name: "password", type: "password", required: true }
 *   ]}
 *   className="max-w-md mx-auto"
 * >
 *   <div className="mt-4">
 *     <Button type="submit">Login</Button>
 *   </div>
 * </FormSimple>
 * ```
 *
 * @param {FormProps} props - The properties for the Form component.
 * @param {FormInputField[]} props.fields - Array of field definitions to automatically render. Each field includes properties like `id`, `label`, `name`, etc.
 * @param {(formData: FormData) => void | Promise<void>} [props.action] - The server action to function to handle form submission.
 * @param {React.ReactNode} [props.children] - Additional content to render inside the form, such as submit buttons or custom inputs.
 * @param {string} [props.className] - CSS class names to customize the form container.
 * @returns {JSX.Element} The rendered form element.
 */
const FormSimple = ({
  fields = [],
  action,
  children,
  className = ""
}: FormProps): JSX.Element => (
  <NextForm className={className} action={action}>
    {fields.length === 0 && !children ? (
      <Text className="text-auth-error">Error: No fields provided</Text>
    ) : (
      fields.map(({ label, className, name, ...inputProps }) => (
        <View className={className} key={inputProps.id ?? name}>
          {label && (
            <Text
              type="label"
              htmlFor={String(name)}
              className="md:text-lg font-medium block mb-4 text-auth-input-label"
            >
              {label}
            </Text>
          )}
          <View className="w-full bg-transparent border rounded-3xl px-6 py-3 transition-colors duration-300 border-auth-border focus-within:border-auth-input-focus">
            <Input name={name} {...inputProps} className="w-full" />
          </View>
        </View>
      ))
    )}
    {children}
  </NextForm>
);

export default FormSimple;
